import React, { useMemo, useRef, useState } from "react";
import moment from "moment";
import { Collapse, Form, message, Select, DatePicker, Button } from "antd";
import HmgMainDropdown from "../../components/Common/DataAttachedElements/HmgDropdown";
import ProjectDropdown from "../../components/Common/DataAttachedElements/ProjectDropdown";
import HotelDropdown from "../../components/Common/DataAttachedElements/HotelDropdown";
import {
  useGetSignStatus,
  useResendFailedEnvelopes,
  useUpdateEnvelopeStatus,
} from "../../hooks/envelope.hooks";
import PaginatedDataTable from "../../components/Common/PaginatedDataTable";
import FailedEnvelopRetryForm from "../../components/EnvelopManager/FailedEnvelopRetry";
import UpdateEnvelopStatus from "../../components/EnvelopManager/UpdateEnvelopStatus";

const { Panel } = Collapse;
const { Option } = Select;
const { RangePicker } = DatePicker;

const APP_TYPES = [
  { label: "Signature", value: "signature" },
  { label: "MyDocs", value: "mydocs" },
];

const formItemLayout = {
  labelCol: {
    xs: { span: 24 },
    sm: { span: 8 },
    md: { span: 8 },
    lg: { span: 6 },
  },
  wrapperCol: {
    xs: { span: 24 },
    sm: { span: 16 },
    md: { span: 12 },
    lg: { span: 10 },
  },
};

const formItemLayoutWithOutLabel = {
  wrapperCol: {
    xs: { span: 24 },
    sm: { offset: 8, span: 16 },
    md: { offset: 8, span: 12 },
    lg: { offset: 6, span: 10 },
  },
};

const formItemLayoutDataTable = {
  wrapperCol: {
    xs: { span: 24 },
    sm: { offset: 8, span: 16 },
    md: { offset: 8, span: 16 },
    lg: { offset: 6, span: 16 },
  },
};

const generateUpdateStatusMessage = (prevState, newState) => {
  if (newState === "VOID") {
    switch (prevState) {
      case "PENDING":
        message.success("Voided Trigger Successful For Pending Envelopes");
        break;
      case "FAILED_UPLOAD":
      case "FAILED_DOWNLOAD":
        message.success("Voided Trigger Successful For Failed Envelopes");
        break;
      case "COMPLETED":
        message.success("Voided Trigger Successful For Completed Envelopes");
        break;
      default:
    }
  }
};

const SignatureQC = () => {
  const [hmg, setHmg] = useState();
  const [project, setProject] = useState();
  const [hotels, setHotels] = useState();
  const [app, setApp] = useState("signature");
  const [dateRange, setDateRange] = useState();

  const statusElmRef = useRef();

  const {
    data: signStatusData,
    mutate: getSignStatus,
    isLoading: isLoadingSignStatus,
  } = useGetSignStatus({
    onSuccess: () => {
      message.success("Data syncing is done !");
    },
    onError: (error) => {
      message.error(
        error?.response?.data?.message ??
        "Failed to retrieve hotel envelope details",
      );
    },
  });

  const retrieveEnvelopeData = () =>
    getSignStatus({
      hmgId: hmg,
      hotels,
      from: moment(dateRange[0]).format("YYYY-MM-DD"),
      to: moment(dateRange[1]).format("YYYY-MM-DD"),
    });

  const { mutate: resendFailedEnvelopes, isLoading: loadingResendAPI } =
    useResendFailedEnvelopes({
      onSuccess: (data) => {
        if(!data?.data){
          message.error(data.message || "Failed to retry failed envelopes");
          return;
        }
        message.success("Retrying Envelops is Successful");
        retrieveEnvelopeData();
      },
      onError: (error) => {
        message.error(
          error?.response?.data?.message ?? "Failed to update envelope status",
        );
      },
    });

  const {
    mutate: updateEnvelopeStatus,
    isLoading: loadingUpdateEnvelopStatus,
  } = useUpdateEnvelopeStatus({
    onSuccess: (apiResponse) => {
      if (apiResponse?.success && apiResponse?.result?.[0]) {
        const sigStatusResponse = apiResponse?.result?.[0];
        generateUpdateStatusMessage(
          sigStatusResponse.prevState,
          sigStatusResponse.newState,
        );
      }
      statusElmRef.current.reset();
      retrieveEnvelopeData();
    },
    onError: (error) => {
      message.error(
        error?.response?.data?.message ?? "Failed to update envelope status",
      );
    },
  });

  const envelopeData = useMemo(() => {
    return signStatusData
      ? signStatusData[app].map((rec) => ({
        ...rec,
        status: rec.state,
        Id:
          app === "signature"
            ? rec.envelopeRequestId
            : rec.qcAppRequestAutoRetryEntityID,
      }))
      : [];
  }, [signStatusData, app]);

  const handleUpdateStatus = (data) => {
    const envelopeVendor = envelopeData.find((item) => item.Id === data.revId)?.vendor;
    updateEnvelopeStatus({
      ...data,
      vendor: envelopeVendor?.toLowerCase(),
    });
  }

  const handleFailedEnvelopeRetry = (data) => {
    const { id } = data;
    // console.log(envelopeData.find((item) => item.Id === id)?.vendor?.toLowerCase());
    resendFailedEnvelopes({ app, id, vendor: envelopeData.find((item) => item.Id === id)?.vendor?.toLowerCase() });
  }

  return (
    <>
      <div className="container_ab">
        <Collapse defaultActiveKey={["1"]} expandIconPosition="end">
          <Panel header="HOTEL ENVELOPE DETAILS" key="1">
            <Form>
              <HmgMainDropdown value={hmg} onChange={setHmg} />
              <ProjectDropdown
                hmgId={hmg}
                value={project}
                onChange={setProject}
              />
              <HotelDropdown
                value={hotels}
                onChange={setHotels}
                projectId={project}
                multiple
              />
              <Form.Item label="Select App" {...formItemLayout}>
                <Select value={app} onChange={setApp} style={{ width: 110 }}>
                  {APP_TYPES.map((apptype) => (
                    <Option value={apptype.value} key={apptype.value}>
                      {apptype.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item {...formItemLayout} label="Date Range" required>
                <RangePicker
                  value={dateRange}
                  disabledDate={(current) =>
                    current > moment().subtract(1, "days")
                  }
                  onChange={setDateRange}
                />
              </Form.Item>
              <Form.Item {...formItemLayoutWithOutLabel}>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={isLoadingSignStatus}
                  disabled={!(hotels?.length > 0 && dateRange)}
                  onClick={() => retrieveEnvelopeData()}
                >
                  Submit
                </Button>
              </Form.Item>
              <Form.Item {...formItemLayoutDataTable}>
                <PaginatedDataTable dataSource={envelopeData} />
              </Form.Item>
            </Form>
          </Panel>
        </Collapse>
      </div>
      <div className="container_ab">
        <Collapse defaultActiveKey={["2"]} expandIconPosition="end">
          <Panel header="Failed Envelope Retry" key="2">
            <FailedEnvelopRetryForm
              filteredRecords={envelopeData}
              resendData={handleFailedEnvelopeRetry}
              isLoading={loadingResendAPI}
            />
          </Panel>
        </Collapse>
      </div>
      <div className="container_ab">
        <Collapse
          defaultActiveKey={["3"]}
          expandIconPosition="end"
          style={{
            display: app === "mydocs" ? "none" : "block",
          }}
        >
          <Panel header="Update Envelope Status" key="3">
            <Form.Item>
              <UpdateEnvelopStatus
                filteredRecords={envelopeData}
                sendCompleteStatus={handleUpdateStatus}
                isLoading={loadingUpdateEnvelopStatus}
                ref={statusElmRef}
              />
            </Form.Item>
          </Panel>
        </Collapse>
      </div>
    </>
  );
};

export default SignatureQC;
